import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, IconButton, TextField } from '@mui/material';
import MinimizeIcon from '@mui/icons-material/Minimize';
import MaximizeIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import SendIcon from '@mui/icons-material/Send';

// Assuming these paths and their types are correct from previous conversions or project setup
import DynamicMessageCard from './components/DynamicMessageCard';
import useVoiceToText from './hooks/useVoiceToText '; // This hook needs to be typed if not already
import StaticMessageCards from './components/StaticMessageCards';
import LoadingSequence from './components/LoadingSequence';
import AIApiService from 'api/AIApiService';
import useApi from 'hooks/useApi';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { getConversationAction, openAiscreen } from 'store/actions/aiActions';

// --- Type Definitions ---

// Type for a single conversation item in the list
interface ConversationItem {
  id: string; // Added a unique id for React keys
  type: 'query' | 'general' | 'explaination' | 'response' | 'data_table' | string; // Extend as needed based on actual message types
  // Changed 'object' to 'Record<string, unknown>'
  msg: string | Record<string, unknown>; // 'msg' can be a string or an object (e.g., for structured data/tables)
}

// Expected return type for useVoiceToText hook
interface UseVoiceToTextResult {
  voiceInputText: string;
  setvoiceInputText: React.Dispatch<React.SetStateAction<string>>;
  startListening: () => void;
  stopListening: () => void;
  isListening: boolean;
}

// Expected return type for SendhQuery service
interface SendhQueryResult {
  query_class: string;
  // Changed 'object' to 'Record<string, unknown>'
  data: string | Record<string, unknown>; // The response data can be a string message or a structured object
  explaination?: string; // Optional explanation
  confidence_level?: string;
}

// --- AiScreen Component ---
const AiScreen = () => {
  const { makeCall } = useApi();
  const { dispatch } = useAppState();
  const { conversationList, aiVisible } = useSelector((state) => state.ai);

  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [conversationListLocal, setConversationList] = useState<ConversationItem[]>([]);
  // Assert the type of useVoiceToText hook return
  const { voiceInputText, setvoiceInputText, startListening, stopListening, isListening } =
    useVoiceToText() as UseVoiceToTextResult;
  const chatContainerRef = useRef<HTMLDivElement>(null); // Explicitly type the ref to HTMLDivElement

  // Function to generate a simple unique ID for messages
  const generateUniqueId = (): string => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleUpdateConversation = (msgType: ConversationItem['type'], msg: ConversationItem['msg']) => {
    const newConversation: ConversationItem = {
      id: generateUniqueId(), // Assign a unique ID
      type: msgType,
      msg,
    };
    setConversationList((prevList) => [...prevList, newConversation]);
    dispatch(getConversationAction([newConversation]));
  };

  const isValidEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailValidation = (): boolean => {
    // If userEmail exists in sessionStorage, immediately return true
    if (sessionStorage.getItem('userEmail')) {
      return true;
    }

    if (isValidEmail(voiceInputText)) {
      const cleanedEmail = voiceInputText.trim();
      sessionStorage.setItem('userEmail', cleanedEmail);
      // handleUpdateConversation('general', 'Successfully logged in with your email. Just let me know how I can assist!');
      setvoiceInputText(''); // Clear input after successful validation and storage
      // After successfully handling the email and updating conversation, return false
      // because the email was just provided, not pre-existing in session storage.
      return false; // Indicating that email was just provided, not pre-existing
    }

    // If the code reaches here, it means isValidEmail(voiceInputText) was false.
    handleUpdateConversation('general', 'Please enter a valid email address to continue.');
    setvoiceInputText('');
    return false; // Return false as email was not valid or not pre-existing
  };

  const handleSendMsg = async () => {
    if (isLoading || voiceInputText.trim() === '') return;
    handleUpdateConversation('query', voiceInputText);

    // handleEmailValidation returns true if email is already in session, or false if it was just set/invalid
    // if (handleEmailValidation() || sessionStorage.getItem('userEmail')) {
    // Re-check sessionStorage.getItem('userEmail')
    setIsLoading(true);
    setvoiceInputText('');

    try {
      // const userEmail = sessionStorage.getItem('userEmail');
      // if (!userEmail) {
      //   // Defensive check in case email somehow disappeared after validation
      //   handleUpdateConversation('general', 'Email is required to send queries. Please provide a valid email.');
      //   setIsLoading(false);
      //   return;
      // }

      const res: SendhQueryResult = await makeCall(AIApiService.SendhQuery({ query: voiceInputText }), 'Error AI');

      // Assert type for SendhQuery as it's an external service call
      // const res: SendhQueryResult = await SendhQuery({
      //   user_email: userEmail,
      //   query: voiceInputText,
      // });

      // Do not clear voiceInputText here if it was already cleared by handleEmailValidation or before SendQuery call
      // setvoiceInputText('');

      // Assuming res.data is the primary response
      handleUpdateConversation(res?.query_class || 'response', res?.data || 'No response data.');
      setIsLoading(false);
      if (res?.explaination !== undefined && res.explaination !== null && res.explaination !== '') {
        handleUpdateConversation('explaination', res.explaination);
      }
      if (res?.confidence_level !== undefined && res.confidence_level !== null && res.confidence_level !== '') {
        handleUpdateConversation('confidencelevel', res.confidence_level);
      }
    } catch (error) {
      console.error('Error sending query:', error);
      setIsLoading(false);
      handleUpdateConversation('general', 'An error occurred while sending your query. Please try again.');
    }
    // }
  };

  const handleVoiceConversation = () => {
    if (isListening) {
      stopListening();
      if (voiceInputText.trim() !== '') {
        // Use .trim() to check for actual content
        handleSendMsg();
      }
    } else {
      startListening();
    }
  };

  // Scroll to bottom of chat container
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversationList, isLoading]); // Also scroll when loading state changes (e.g., when LoadingSequence appears)

  const closeFn = () => {
    dispatch(openAiscreen(false));
    setConversationList([]);
    sessionStorage.removeItem('userEmail');
  };
  return (
    <>
      {aiVisible && (
        <Box
          // Outer container mirroring the Tailwind div
          sx={{
            position: 'absolute',
            bottom: 0,
            backgroundColor: 'background.paper', // Uses theme's paper background (usually white)
            border: '1px solid',
            borderColor: 'grey.300',
            boxShadow: 3, // MUI's default shadows (elevation)
            borderRadius: 2, // MUI's default border radius
            transition: 'all 500ms ease', // Smooth transition for width/position changes
            zIndex: 1300, // Ensure it's above most other content, like AppBar/Drawer
            // Conditional styles for maximized/minimized state
            ...(isMaximized
              ? {
                  right: '50%',
                  transform: 'translateX(50%)',
                  width: '1110px',
                  height: '90vh', // Adjust height for maximized view if needed
                  maxHeight: '90vh', // Prevent overflow
                }
              : {
                  right: 0,
                  width: '400px',
                  height: 'auto', // Adjust height as per content or set a fixed min-height
                }),
          }}
        >
          {/* Top blue bar */}
          <Box
            sx={{
              borderRadius: '8px 8px 0 0', // Top-left and top-right rounded corners
              width: '100%',
              height: '9px',
              backgroundColor: '#004DE8',
            }}
          />

          {/* Header section (eDelivery title and icons) */}
          <Box sx={{ padding: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="h6" // Or 'subtitle1', 'body1' depending on desired size
              sx={{ fontWeight: 'fontWeightSemiBold' }}
            >
              eDelivery AI Agent - powered by MIGSO-PCUBED
            </Typography>

            <Box sx={{ display: 'flex', gap: 2.5 }}>
              <IconButton onClick={() => setIsMaximized((prev) => !prev)} size="small">
                {isMaximized ? <MinimizeIcon fontSize="small" /> : <MaximizeIcon fontSize="small" />}
              </IconButton>
              <IconButton onClick={() => closeFn()} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Horizontal divider */}
          <Box sx={{ height: '1px', backgroundColor: 'grey.200' }} />

          {/* Main content area (messages) */}
          <Box
            sx={{
              height: isMaximized ? 'calc(100vh - 180px)' : 'calc(100% - 180px)', // Adjust height based on header/footer
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              ref={chatContainerRef}
              sx={{
                height: '65vh', // Or calc(90vh - 180px) in maximized state
                flexGrow: 1, // Allows it to take available height
                overflowY: 'auto',
                padding: 2.5, // Equivalent to p-5 for overall padding
                paddingLeft: isMaximized ? '210px' : 2.5, // Conditional padding for maximized view
                paddingRight: isMaximized ? '210px' : 2.5,
                display: 'flex', // Use flex to stack messages
                flexDirection: 'column', // Stack messages vertically
                gap: 1.5, // Add gap between messages
              }}
            >
              <StaticMessageCards isMaximized={isMaximized} />
              <DynamicMessageCard isMaximized={isMaximized} />

              {isLoading && (
                <Box sx={{ width: '80px', alignSelf: 'flex-start' }}>
                  <LoadingSequence />
                </Box>
              )}
            </Box>

            {/* Second horizontal divider */}
            <Box sx={{ height: '1px', backgroundColor: 'grey.700', marginX: isMaximized ? '210px' : 0 }} />

            {/* Input area */}
            <Box sx={{ minHeight: '80px', padding: 2, marginX: isMaximized ? '210px' : 0 }}>
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  gap: 2, // gap-2 = 8px
                  border: '2px solid',
                  borderColor: '#E5E5E5', // Equivalent to gray-200/300-like custom border
                  borderRadius: 1, // rounded-md is usually 4px (MUI default is 4px)
                  alignItems: 'center', // Align items vertically in the center
                }}
              >
                {/* Textarea for input */}
                <TextField
                  fullWidth // Takes up available width
                  multiline // For textarea behavior
                  rows={2} // Start with one row
                  maxRows={4} // Allow up to 4 rows before scrolling
                  value={voiceInputText}
                  onChange={(e) => setvoiceInputText(e.target.value)}
                  placeholder="Ask me something..."
                  variant="standard" // Or 'outlined', 'filled' - 'standard' has no border by default
                  InputProps={{
                    disableUnderline: true, // Remove default TextField underline
                    style: {
                      paddingLeft: 10, // Adjust this value to move the cursor further right
                    },
                    sx: {
                      border: 'none',
                      paddingLeft: 2, // pl-4 = 16px (MUI 2 units)
                      paddingTop: 1, // pt-2 = 8px (MUI 1 unit)
                      lineHeight: '2rem', // leading-6 = 24px, 1.5rem is typical for 16px font
                      //   fontSize: '1rem', // text-base
                      '& textarea': {
                        // Target the actual textarea element for resize property
                        fontWeight: 400,
                        resize: 'none', // Equivalent to resize-none
                        outline: 'none', // focus:outline-none
                        '&:focus': {
                          outline: 'none', // Ensure outline is none on focus
                          boxShadow: 'none', // Remove any default focus shadow
                        },
                      },
                    },
                  }}
                  sx={{
                    flexGrow: 1, // Ensures TextField expands to fill space
                    // Override default TextField padding/margin
                    '& .MuiInputBase-root': {
                      padding: 0,
                    },
                  }}
                />

                {/* Mic/Send button */}
                <IconButton
                  onClick={voiceInputText.trim() === '' || isListening ? handleVoiceConversation : handleSendMsg}
                  sx={{
                    marginRight: 2, // mr-2 = 8px (MUI 2 units)
                    opacity: isListening ? 0.75 : 1, // Conditional opacity for mic icon
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                >
                  {voiceInputText.trim() === '' || isListening ? <MicIcon /> : <SendIcon />}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AiScreen;
